import * as React from 'react';
import { useEffect, useRef } from 'react';
import { ICusSubCategoryLinksData } from './cus-sub-category-links.data';
import { ICusSubCategoryLinksProps } from './cus-sub-category-links.props.autogenerated';

export interface ICusSubCategoryLinksViewProps extends ICusSubCategoryLinksProps<ICusSubCategoryLinksData> {}

export default function CusSubCategoryLinks({ data }: ICusSubCategoryLinksProps<ICusSubCategoryLinksData>): JSX.Element | null {
    const { currentCategory, categoriesFamily, parentCategory } = data.currentCategoryData.result ?? {
        currentCategory: null,
        categoriesFamily: [],
        parentCategory: null
    };

    const containerRef = useRef<HTMLUListElement | null>(null);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const container = containerRef.current;

        let isPointerDown = false;
        let startX = 0;
        let scrollStart = 0;
        let hasDragged = false;

        const DRAG_THRESHOLD = 6;

        const isInteractiveElement = (el: EventTarget | null): boolean => {
            if (!(el instanceof HTMLElement)) {
                return false;
            }

            return !!el.closest('a, button, input, textarea, select');
        };

        const onMouseDown = (e: MouseEvent) => {
            if (!isInteractiveElement(e.target)) {
                isPointerDown = true;
                hasDragged = false;
                startX = e.pageX;
                scrollStart = container.scrollLeft;
            }
        };

        const onMouseMove = (e: MouseEvent) => {
            if (!isPointerDown) {
                return;
            }

            const deltaX = e.pageX - startX;

            if (Math.abs(deltaX) > DRAG_THRESHOLD) {
                hasDragged = true;
                container.classList.add('is-dragging');
                container.scrollLeft = scrollStart - deltaX;
                e.preventDefault();
            }
        };

        const stopDrag = () => {
            isPointerDown = false;
            container.classList.remove('is-dragging');
        };

        const onClickCapture = (e: MouseEvent) => {
            if (hasDragged) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        container.addEventListener('mousedown', onMouseDown);
        container.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', stopDrag);
        container.addEventListener('click', onClickCapture, true);

        return () => {
            container.removeEventListener('mousedown', onMouseDown);
            container.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', stopDrag);
            container.removeEventListener('click', onClickCapture, true);
        };
    }, []);

    return (
        <nav>
            <ul className='bs-subcategory__container' ref={containerRef}>
                {!!parentCategory?.ParentCategory && (
                    <a href={parentCategory.Url}>
                        <li className='bs-subcategory__parent' key={currentCategory?.RecordId}>
                            <p>←</p>
                            <p>{parentCategory.Name}</p>
                        </li>
                    </a>
                )}

                <a>
                    <li className='bs-subcategory__item --active' key={currentCategory?.RecordId}>
                        <p>{currentCategory?.Name}</p>
                    </li>
                </a>
                {categoriesFamily?.length > 0 &&
                    categoriesFamily.map(category => {
                        if (category.Name === currentCategory?.Name) {
                            return null;
                        }

                        return (
                            <a href={category.Url} key={category.RecordId}>
                                <li className='bs-subcategory__item'>{category.Name}</li>
                            </a>
                        );
                    })}
            </ul>
        </nav>
    );
}
